import React from "react";
import classNames from "classnames";
import type { FieldValues } from "react-hook-form";
import {
    BusinessForm,
    type BusinessFormProps,
    type BusinessFormRenderProps,
    type FormHandle,
} from "@src/components/business-form";
import FormFooter from "@shared/ui/form-footer";
import { useFormPanelHost } from "./FormPanelHostContext";
import "./FormPanel.scss";

function callIfFn<TContext, TResult>(value: TResult | ((context: TContext) => TResult), context: TContext): TResult {
    return typeof value === "function" ? (value as (ctx: TContext) => TResult)(context) : value;
}

/** Render-пропсы {@link FormPanel}. */
export type FormPanelRenderProps<TFieldValues extends FieldValues> = BusinessFormRenderProps<TFieldValues> & {
    disabled: boolean;
    /**
     * `true`, пока `onSubmit` формы ещё не завершился (RHF `isSubmitting`). \
     * Для disable полей / pause scanner и т.п. внутри body — не опирайтесь на `mutation.isPending` \
     * из props `windows.open` (там снимок).
     */
    isFormSubmitting: boolean;
};

/** Контекст кастомного footer. */
export interface FormPanelFooterRenderContext {
    onRequestDismiss?: () => void;
    /** Submit с учётом host (`closeOnSubmit` и т.п.). */
    submitForm: () => void;
    hasUnsavedChanges: boolean;
    /**
     * `true`, пока `onSubmit` формы ещё не завершился (RHF `isSubmitting`). \
     * Используйте для `confirmLoading` / disable в кастомном footer — в т.ч. внутри overlay window, \
     * где `mutation.isPending` из props `windows.open` — застывший снимок и не обновляется.
     */
    isFormSubmitting: boolean;
}

/** Пропсы {@link FormPanel} — форма + footer, без overlay/window shell. */
export interface FormPanelProps<TFieldValues extends FieldValues = FieldValues> {
    /** `testId` корневой {@link BusinessForm}. */
    formTestId?: string;
    /** Ref API вложенной {@link BusinessForm}. */
    formApiRef?: React.MutableRefObject<FormHandle<TFieldValues> | null> | ((api: FormHandle<TFieldValues> | null) => void);
    /**
     * Пропсы вложенной {@link BusinessForm}. \
     * `disabled` задавайте только на {@link FormPanelProps.disabled}, чтобы footer и поля отключались синхронно.
     */
    formProps: Omit<BusinessFormProps<TFieldValues>, "children" | "disabled" | "testId">;
    /** Контент формы или render-prop с API вложенной {@link BusinessForm}. */
    children: React.ReactNode | ((form: FormPanelRenderProps<TFieldValues>) => React.ReactNode);
    /** Кастомный footer; если не задан, используется default footer. */
    footer?: React.ReactNode | ((context: FormPanelFooterRenderContext) => React.ReactNode);
    /** Включает default footer с кнопкой submit. */
    useDefaultFooter?: boolean;
    /** Разрешает submit без отключения всех полей формы. */
    canSubmit?: boolean;
    /** Отключает поля вложенной формы и submit-кнопку footer. */
    disabled?: boolean;
    /** Fallback на время `formProps.ready === false`. */
    loadingFallback?: React.ReactNode;
    /** Локальный dismiss (страница без overlay). В window — задаёт host. */
    onRequestDismiss?: () => void;
    /** Дополнительный CSS-класс корневого контейнера. */
    className?: string;
    /** Дополнительный CSS-класс scroll/body-контейнера. */
    contentClassName?: string;
    /** Дополнительный CSS-класс footer-контейнера. */
    footerClassName?: string;
}

/**
 * Связывает тело {@link BusinessForm} с footer действий формы.
 * Можно использовать на плоском экране и внутри overlay content.
 */
export const FormPanel = <TFieldValues extends FieldValues = FieldValues>({
    formTestId,
    formApiRef,
    formProps,
    children,
    footer,
    useDefaultFooter = true,
    canSubmit = true,
    disabled = false,
    loadingFallback = "Загрузка формы...",
    onRequestDismiss,
    className,
    contentClassName,
    footerClassName,
}: FormPanelProps<TFieldValues>) => {
    const host = useFormPanelHost();
    const internalFormApiRef = React.useRef<FormHandle<TFieldValues> | null>(null);
    const formSubmittingUnsubRef = React.useRef<(() => void) | null>(null);
    const bodyRef = React.useRef<HTMLDivElement | null>(null);
    const [isFormSubmitting, setIsFormSubmitting] = React.useState(false);

    const isFormReady = formProps.ready ?? true;
    const effectiveDisabled = disabled || !isFormReady || isFormSubmitting;
    const effectiveSubmitDisabled = effectiveDisabled || !canSubmit;

    const resolvedOnRequestDismiss = onRequestDismiss ?? host?.onRequestDismiss;
    const hasUnsavedChanges = host?.dirtyTrackingEnabled === true ? host.hasUnsavedChanges : false;

    const assignBodyRef = React.useCallback(
        (element: HTMLDivElement | null) => {
            bodyRef.current = element;
            if (host?.dirtyTrackingEnabled === true) {
                host.setDirtyTrackingRoot(element);
            }
        },
        [host],
    );

    const resolvedOnSubmit = React.useMemo(() => {
        const baseOnSubmit = formProps.onSubmit;
        if (host == null || baseOnSubmit == null) {
            return baseOnSubmit;
        }

        return host.wrapOnSubmit(baseOnSubmit);
    }, [formProps.onSubmit, host]);

    const effectiveFormProps: Omit<BusinessFormProps<TFieldValues>, "children" | "testId"> = {
        ...formProps,
        onSubmit: resolvedOnSubmit,
        className: classNames("form_panel_root", formProps.className),
        disabled: effectiveDisabled,
        fallback: formProps.fallback ?? loadingFallback,
        submitOnCtrlEnter: effectiveSubmitDisabled ? false : (formProps.submitOnCtrlEnter ?? true),
    };

    const assignFormApiRef = React.useCallback(
        (instance: FormHandle<TFieldValues> | null) => {
            formSubmittingUnsubRef.current?.();
            formSubmittingUnsubRef.current = null;
            internalFormApiRef.current = instance;

            if (instance != null) {
                formSubmittingUnsubRef.current = instance.subscribeSubmitting(setIsFormSubmitting);
            } else {
                setIsFormSubmitting(false);
            }

            if (typeof formApiRef === "function") {
                formApiRef(instance);
                return;
            }

            if (formApiRef != null) {
                formApiRef.current = instance;
            }
        },
        [formApiRef],
    );

    React.useEffect(() => {
        return () => {
            formSubmittingUnsubRef.current?.();
        };
    }, []);

    const resolvedChildren: BusinessFormProps<TFieldValues>["children"] =
        typeof children === "function"
            ? (baseForm) => children({ ...baseForm, disabled: effectiveDisabled, isFormSubmitting })
            : children;

    const submitForm = React.useCallback(() => {
        if (resolvedOnSubmit == null) {
            return;
        }

        void internalFormApiRef.current?.handleSubmit(resolvedOnSubmit)();
    }, [resolvedOnSubmit]);

    const footerRenderContext = React.useMemo<FormPanelFooterRenderContext>(
        () => ({
            onRequestDismiss: resolvedOnRequestDismiss,
            submitForm,
            hasUnsavedChanges,
            isFormSubmitting,
        }),
        [hasUnsavedChanges, isFormSubmitting, resolvedOnRequestDismiss, submitForm],
    );

    const resolvedFooter =
        footer ??
        (useDefaultFooter
            ? ({ isFormSubmitting: submitting }: FormPanelFooterRenderContext) => (
                  <FormFooter
                      confirmLoading={!isFormReady || submitting}
                      confirmDisabled={effectiveSubmitDisabled}
                      onConfirm={submitForm}
                  />
              )
            : undefined);

    const footerNode = resolvedFooter != null ? callIfFn(resolvedFooter, footerRenderContext) : null;

    return (
        <div className={classNames("form_panel", className)}>
            <div ref={assignBodyRef} className={classNames("form_panel__body", "custom_scrollbar", contentClassName)}>
                <BusinessForm ref={assignFormApiRef} {...effectiveFormProps} testId={formTestId}>
                    {resolvedChildren}
                </BusinessForm>
            </div>
            {footerNode != null && <div className={classNames("form_panel__footer", footerClassName)}>{footerNode}</div>}
        </div>
    );
};
